import { useEffect, useMemo, useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useToast } from '@/components/ui/Toast';
import { campaignBudgetApiErrorMessage } from '@/api/campaignBudgetUpdate';
import { saveCampaignSettings, type CampaignSettingsPatch } from '@/api/campaignSettingsUpdate';
import type { CampaignAdProduct, SpCampaignNormalized } from '@/types/spCampaigns';
import {
  extractCampaignPlacementRows,
  PLACEMENT_BID_PERCENT_MAX,
  PLACEMENT_BID_PERCENT_MIN,
  validatePlacementBidPercent,
  type CampaignPlacementTableRow,
} from '@/utils/campaignPlacementRows';

type CampaignPlacementsSectionProps = {
  profileId: string;
  campaign: SpCampaignNormalized;
  adProduct: CampaignAdProduct;
  /** Refresh the campaign after placements are saved. */
  onUpdated?: () => void;
};

/** Input values keyed by row id, seeded from the campaign's current percentages. */
function draftsFromRows(rows: CampaignPlacementTableRow[]): Record<string, string> {
  const drafts: Record<string, string> = {};
  for (const row of rows) {
    if (row.editableKey) drafts[row.id] = row.percentage == null ? '' : String(row.percentage);
  }
  return drafts;
}

export function CampaignPlacementsSection({
  profileId,
  campaign,
  adProduct,
  onUpdated,
}: CampaignPlacementsSectionProps) {
  const { showToast } = useToast();
  const rows = useMemo(
    () => extractCampaignPlacementRows(campaign.raw, adProduct),
    [campaign.raw, adProduct]
  );
  const [drafts, setDrafts] = useState(() => draftsFromRows(rows));
  const [formError, setFormError] = useState<string | null>(null);

  // Reload the inputs when the campaign data changes (e.g. after a save refetch).
  useEffect(() => {
    setDrafts(draftsFromRows(rows));
  }, [rows]);

  const editableRows = rows.filter((row) => row.editableKey);
  const changedRows = editableRows.filter(
    (row) => drafts[row.id]?.trim() !== (row.percentage == null ? '' : String(row.percentage))
  );
  const rowErrors = Object.fromEntries(
    changedRows.map((row) => [row.id, validatePlacementBidPercent(drafts[row.id] ?? '')])
  );
  const hasErrors = Object.values(rowErrors).some(Boolean);

  const mutation = useMutation({
    mutationFn: (placements: NonNullable<CampaignSettingsPatch['placements']>) =>
      saveCampaignSettings(profileId, campaign.campaignId, adProduct, { placements }),
    onSuccess: () => {
      setFormError(null);
      showToast('Placement bid adjustments updated', 'success');
      onUpdated?.();
    },
    onError: (error) => {
      const message = campaignBudgetApiErrorMessage(error, 'Failed to update placements');
      setFormError(message);
      showToast(`Failed to update placements: ${message}`, 'error');
    },
  });

  const save = () => {
    if (changedRows.length === 0 || hasErrors) return;
    const placements: NonNullable<CampaignSettingsPatch['placements']> = {};
    for (const row of changedRows) {
      if (row.editableKey) placements[row.editableKey] = Number(drafts[row.id].trim());
    }
    mutation.mutate(placements);
  };

  return (
    <div>
      <h2 className="text-lg font-semibold text-foreground mb-3">Placements</h2>
      {rows.length === 0 ? (
        <p className="text-sm text-foreground-subtle">No placement bidding rules.</p>
      ) : (
        <>
          <div className="max-h-80 overflow-x-auto overflow-y-auto rounded-xl border border-border">
            <table className="min-w-full text-sm">
              <thead>
                <tr className="bg-page text-left text-xs font-medium text-foreground-muted uppercase tracking-wide">
                  <th className="px-3 py-2">Placement</th>
                  <th className="px-3 py-2 text-center">Bid adjustment</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-border">
                {rows.map((row) => (
                  <tr key={row.id} className="bg-surface hover:bg-page/80">
                    <td className="px-3 py-2 text-foreground align-middle">{row.label}</td>
                    <td className="px-3 py-2 text-foreground-muted text-center align-middle">
                      {row.editableKey ? (
                        <div className="inline-flex flex-col items-center gap-1">
                          <div className="inline-flex items-center gap-1">
                            <Input
                              type="number"
                              inputMode="numeric"
                              min={PLACEMENT_BID_PERCENT_MIN}
                              max={PLACEMENT_BID_PERCENT_MAX}
                              step={1}
                              value={drafts[row.id] ?? ''}
                              disabled={mutation.isPending}
                              onChange={(event) => {
                                setDrafts((prev) => ({ ...prev, [row.id]: event.target.value }));
                                setFormError(null);
                              }}
                              aria-label={`${row.label} bid adjustment percent`}
                              aria-invalid={rowErrors[row.id] != null}
                              className="h-7 w-[4.5rem] px-2 py-1 text-center font-mono text-sm tabular-nums"
                            />
                            <span className="text-sm" aria-hidden>
                              %
                            </span>
                          </div>
                          {rowErrors[row.id] ? (
                            <p className="max-w-[14rem] text-center text-xs text-error">
                              {rowErrors[row.id]}
                            </p>
                          ) : null}
                        </div>
                      ) : (
                        <span
                          className="font-mono text-sm tabular-nums"
                          title={row.readOnlyReason ?? undefined}
                        >
                          {row.percentageLabel}
                        </span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {editableRows.length > 0 ? (
            <div className="mt-3 flex flex-wrap items-center justify-end gap-2">
              {formError ? <p className="mr-auto text-sm text-error">{formError}</p> : null}
              <Button
                variant="ghost"
                size="sm"
                disabled={changedRows.length === 0 || mutation.isPending}
                onClick={() => {
                  setDrafts(draftsFromRows(rows));
                  setFormError(null);
                }}
              >
                Reset
              </Button>
              <Button
                size="sm"
                disabled={changedRows.length === 0 || hasErrors || mutation.isPending}
                onClick={save}
              >
                {mutation.isPending ? (
                  <>
                    <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" aria-hidden />
                    Saving…
                  </>
                ) : (
                  'Save placements'
                )}
              </Button>
            </div>
          ) : null}
        </>
      )}
    </div>
  );
}
